import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type DriverGridRowView, type DriverGridView, type VehicleClassView, type WorkLineView, VEHICLE_CLASS_VIEWS, WORK_LINE_VIEWS } from '@vello/api-client';
import { driverRegistryAuthority, formatMarketInstant, marketTimeZone } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { MarketField } from '../components/market-field';
import { RevealContact } from '../components/reveal-contact';
import { useDebounced } from '../components/use-debounced';
import { WorkLineChips } from '../components/work-lines';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormRow, EmptyState, Text, PanelBody, Row } from '@vello/web-theme';
const PAGE_SIZE = 25;

/**
 * **Motoristas › Cadastro** (W9-DRIVER-REGISTRY, D481, ADR-0099; DevMobility D-021 "Grid de prestadores (colunas)", D-022
 * "Ações por prestador") — the market's driver grid over `GET /v1/admin/driver-registry` (`driver:view`, GEO).
 *
 * - The **market** comes from the operator's scope (never typed). The search finds a name, a map identifier, an e-mail or the
 *   last digits of a phone; the status filter and "documents waiting" narrow the list; the grid is paged.
 * - D621: "Atua em" says what each partner works in (car or moto rides, food, parcels — one person may do several) and the
 *   "Atua em" / "Veículo" filters narrow the grid to one line of work or one class of approved vehicle (server-side, paged).
 * - Each row names the driver (and his map identifier), the LAST FOUR digits of his phone, his group, the account status, how
 *   many documents are approved or waiting, the vehicles (active / waiting inspection) and the age of the oldest active
 *   one in years, and when he was registered in the market's zone. "Abrir" opens his record (checklist, registration,
 *   evidence, documents, payment methods). A gender is never a column or a filter.
 * - The screen's one primary action is **Cadastrar motorista** (`driver:register`); whoever lacks the word does not see it.
 *   A refused read is an error, never an empty list. No native dialog is ever opened.
 */
export function DriverRegistryScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, refusal: refusalOf } = useVocab();
  const { navigate } = useRouter();
  const { mayRegister } = driverRegistryAuthority(grants);
  const [market, setMarket] = useState('');
  const [q, setQ] = useState('');
  const qSettled = useDebounced(q.trim(), 400);
  const [status, setStatus] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [workLine, setWorkLine] = useState<WorkLineView | ''>('');
  const [vehicleClass, setVehicleClass] = useState<VehicleClassView | ''>('');
  const [page, setPage] = useState(0);
  const [loaded, setLoaded] = useState<string | undefined>(undefined);
  const [view, setView] = useState<DriverGridView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const seq = useRef(0);

  const load = useCallback(
    async (m: string, p: number, background = false): Promise<void> => {
      const n = ++seq.current;
      // A background tick keeps the rows on screen; a new market / filter / page starts from a clean grid.
      if (!background) {
        setError(undefined);
        setLoaded(m);
        setView(undefined);
      }
      if (m === '') return;
      const res = await runtime.driverRegistry.grid({
        marketId: m,
        ...(qSettled.length >= 2 ? { q: qSettled } : {}),
        ...(status !== '' ? { status: status as 'ACTIVE' | 'SUSPENDED' } : {}),
        ...(waiting ? { documentsWaiting: true } : {}),
        ...(workLine !== '' ? { workLine } : {}),
        ...(vehicleClass !== '' ? { vehicleClass } : {}),
        limit: PAGE_SIZE,
        offset: p * PAGE_SIZE,
      });
      if (n !== seq.current) return;
      if (res.ok) {
        setError(undefined);
        setView(res.value);
      } else setError(refusalOf('refusal.admin.driver_registry', res.error) ?? t('registry.refusal.UNKNOWN'));
    },
    [runtime, qSettled, status, waiting, workLine, vehicleClass, t],
  );

  // The grid follows the scoped market and the filters by itself (typed text settles ~400 ms), and stays current.
  useEffect(() => {
    setPage(0);
    void load(market, 0);
  }, [market, load]);
  useAutoRefresh(() => load(market, page, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const tz = marketTimeZone(loaded, markets);
  const total = view?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <PageHeader
        title={t('registry.grid.title')}
        subtitle={t('registry.grid.subtitle')}
        actions={mayRegister ? <Button variant="primary" onClick={() => navigate('driver_register')}>{t('registry.grid.register')}</Button> : undefined}
      />

      <Panel>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormRow action={<CheckRow id="reg-waiting" checked={waiting} onChange={setWaiting}>{t('registry.grid.waiting')}</CheckRow>}>
            <MarketField id="reg-market" value={market} onChange={setMarket} />
            <Field label={t('registry.grid.search')} htmlFor="reg-q" hint={t('registry.grid.search.hint')}>
              <TextInput id="reg-q" value={q} autoComplete="off" onChange={(e) => setQ(e.target.value)} />
            </Field>
            <Field label={t('registry.grid.status')} htmlFor="reg-status">
              <Select id="reg-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{t('registry.grid.status.all')}</option>
                <option value="ACTIVE">{v('identity.user_status', 'ACTIVE')}</option>
                <option value="SUSPENDED">{v('identity.user_status', 'SUSPENDED')}</option>
              </Select>
            </Field>
            <Field label={t('worklines.filter')} htmlFor="reg-work-line">
              <Select id="reg-work-line" value={workLine} onChange={(e) => setWorkLine(e.target.value as WorkLineView | '')}>
                <option value="">{t('worklines.filter.all')}</option>
                {WORK_LINE_VIEWS.map((l) => <option key={l} value={l}>{v('driver.work_line', l)}</option>)}
              </Select>
            </Field>
            <Field label={t('worklines.vehicle')} htmlFor="reg-vehicle-class">
              <Select id="reg-vehicle-class" value={vehicleClass} onChange={(e) => setVehicleClass(e.target.value as VehicleClassView | '')}>
                <option value="">{t('worklines.vehicle.all')}</option>
                {VEHICLE_CLASS_VIEWS.map((c) => <option key={c} value={c}>{v('service_categories.vehicle_class', c)}</option>)}
              </Select>
            </Field>
          </FormRow>
        </form>
        {loaded === '' && <Notice tone="neutral">{t('registry.marketHint')}</Notice>}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
      </Panel>

      <section aria-label={t('registry.grid.list')}>
        <Panel padded={false}>
          <Table
            columns={[
              {
                key: 'person',
                header: t('registry.col.driver'),
                render: (d: DriverGridRowView) => (
                  <span>
                    {d.name ?? '—'}
                    <Text role="caption" tone="tertiary" mono block>{d.mapIdentifier !== undefined ? t('registry.col.mapId', { id: d.mapIdentifier }) : t('registry.col.noMapId')}</Text>
                  </span>
                ),
              },
              { key: 'workLines', header: t('worklines.col'), render: (d: DriverGridRowView) => <WorkLineChips lines={d.workLines} /> },
              { key: 'phone', header: t('registry.col.phone'), render: (d: DriverGridRowView) => <RevealContact userId={d.driverId} last4={d.phoneLast4} /> },
              { key: 'group', header: t('registry.col.group'), render: (d: DriverGridRowView) => <span>{d.groupName ?? '—'}</span> },
              {
                key: 'status',
                header: t('registry.col.status'),
                render: (d: DriverGridRowView) => <Badge tone={d.accountStatus === 'ACTIVE' ? 'success' : 'danger'}>{v('identity.user_status', d.accountStatus)}</Badge>,
              },
              {
                key: 'documents',
                header: t('registry.col.documents'),
                render: (d: DriverGridRowView) => (
                  <span className="tnum">
                    {t('registry.col.documents.value', { approved: String(d.documentsApproved) })}
                    {d.documentsWaiting > 0 && <Badge tone="warning">{t('registry.col.documents.waiting', { count: String(d.documentsWaiting) })}</Badge>}
                  </span>
                ),
              },
              {
                key: 'vehicles',
                header: t('registry.col.vehicles'),
                render: (d: DriverGridRowView) => (
                  <span className="tnum">
                    {t('registry.col.vehicles.value', { active: String(d.vehiclesActive), waiting: String(d.vehiclesWaiting) })}
                    {d.vehicleAgeYears !== undefined && <Text role="caption" tone="tertiary" block>{t('registry.col.vehicles.age', { years: String(d.vehicleAgeYears) })}</Text>}
                  </span>
                ),
              },
              { key: 'since', header: t('registry.col.since'), render: (d: DriverGridRowView) => <span className="tnum">{formatMarketInstant(d.createdAt, tz, lang, 'date')}</span> },
            ]}
            rows={view?.drivers ?? []}
            rowKey={(d) => d.driverId}
            onRowClick={(d) => navigate('driver_detail', { id: d.driverId })}
            loading={loaded !== undefined && loaded !== '' && view === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('ui.loadFailed') : undefined}
            onRetry={() => void load(market, page)}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={loaded === undefined || loaded === '' ? t('registry.marketHint') : t('registry.grid.empty')} />}
          />
          {view !== undefined && total > PAGE_SIZE && (
            <PanelBody><Row gap={2} align="center">
              <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => { setPage(page - 1); void load(market, page - 1); }}>{t('registry.page.prev')}</Button>
              <Text role="caption" tone="tertiary" block>{t('registry.page.of', { page: String(page + 1), pages: String(pages), total: String(total) })}</Text>
              <Button size="sm" variant="ghost" disabled={page + 1 >= pages} onClick={() => { setPage(page + 1); void load(market, page + 1); }}>{t('registry.page.next')}</Button>
            </Row></PanelBody>
          )}
        </Panel>
      </section>
    </>
  );
}
